import { Observable, Subject } from 'rxjs';
import { RequestCriteria } from '../../model/request-criteria';
import { SessionStorageService } from 'src/app/common/service/session-storage.service';
import { map } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

export class FilterHistoryManager {
    readonly filterHistoryPrefix = 'FILTER_HISTORY_';
    private recentIdx: number = 0;
    private criteria: CriteriaHistoryEntry[] = [];
    private storageName?: string;
    private inMemory: Record<string, RequestCriteria> = {};

    private navigate = new Subject<CriteriaHistoryEntry>();
    private change = new Subject();
    changeSub$ = this.change.asObservable();

    constructor(
        private storage: SessionStorageService,
        private size: number,
        private key: string
    ) {
        this.storageName =
            `${this.filterHistoryPrefix}${key ?? 'default'}`;
        this.criteria = this.storageName
            ? (this.storage.getItemArray(this.storageName, CriteriaHistoryEntry) ??
                [])
            : [];
        if (this.criteria.length > this.size) this.criteria.splice(this.size);
        this.inMemory = this.clone(this.recent?.slot);
    }

    put(clientId: string, criteria: RequestCriteria): void {
        if (criteria) {
            this.inMemory[clientId] = cloneDeep(criteria);
        }
    }

    getRecent(clientId: string): RequestCriteria {
        return this.recent?.slot?.[clientId]
            ? cloneDeep(this.recent.slot[clientId])
            : null;
    }

    getHistory(): CriteriaHistoryEntry[] {
        return cloneDeep(this.criteria);
    }

    private get last(): CriteriaHistoryEntry {
        return this.criteria?.[0]
            ? this.criteria[0]
            : null;
    }

    private get recent(): CriteriaHistoryEntry {
        return this.criteria?.[this.recentIdx]
            ? this.criteria[this.recentIdx]
            : null;
    }

    get recentTimestamp(): Date {
        return this.recent
            ? this.recent.timestamp
            : null;
    }

    get hasNext(): boolean {
        return this.recentIdx > 0;
    }

    get hasPrevious(): boolean {
        return this.recentIdx < this.criteria.length - 1;
    }

    get isEmpty(): boolean {
        return this.criteria.length === 0;
    }

    get historyChain(): Date[] {
        return this.criteria.map(h => h.timestamp);
    }

    get recentId(): number {
        return this.recentIdx;
    }

    next(): void {
        if (this.hasNext) {
            this.recentIdx -= 1;
            this.inMemory = this.clone(this.recent?.slot);
            this.navigate.next(cloneDeep(this.recent));
        }
    }

    previous(): void {
        if (this.hasPrevious) {
            this.recentIdx += 1;
            this.inMemory = this.clone(this.recent?.slot);
            this.navigate.next(cloneDeep(this.recent));
        }
    }

    save(): void {
        const newHistory = this.update();
        if (newHistory) {
            this.storage.setItem(this.storageName, newHistory);
            this.change.next();
        }
    }

    setRecent(idx: number): void {
        if (idx < -1 || idx > this.criteria.length - 1) {
            console.error('Index not in history size range');
        } else if (idx !== this.recentIdx) {
            this.recentIdx = idx;
            this.inMemory = this.clone(this.recent?.slot);
            this.navigate.next(cloneDeep(this.recent));
        }
    }

    clear(): void {
        this.criteria = [];
        this.recentIdx = 0;
        this.storage.removeItem(this.storageName);
        this.change.next();
    }

    reset(withoutInit: boolean = false): void {
        this.recentIdx = withoutInit ? -1 : 0;
        this.navigate.next(cloneDeep(this.recent));
    }

    getNavigateObs$(clientId: string): Observable<RequestCriteria> {
        return this.navigate.asObservable().pipe(map((his) => his?.slot[clientId]));
    }

    private update(): CriteriaHistoryEntry[] {
        if (this.inMemory) {
            const last = this.last;
            const jsonCrit = JSON.stringify(this.inMemory);
            if (jsonCrit === JSON.stringify(last?.slot)) last.timestamp = new Date();
            else {
                if (jsonCrit === JSON.stringify(this.recent?.slot))
                    this.criteria.splice(this.recentId, 1);
                const newEntry = new CriteriaHistoryEntry();
                newEntry.timestamp = new Date();
                newEntry.slot = cloneDeep(this.inMemory);
                const size = this.criteria.unshift(newEntry);
                if (size > this.size) this.criteria.pop();
            }
            // Sam indeks na początek łańcucha, bez emisji navigate: zapisany wpis jest tym, co już siedzi
            // w kreatorze, a navigate przebudowałby formularz (setInitialFilters -> initForm) i przy
            // autozatwierdzaniu zapętlił się przez valueChanges.
            this.recentIdx = 0;
            return this.criteria;
        }
        this.reset();
        return null;
    }

    clone(
        value: Record<string, RequestCriteria>,
    ): Record<string, RequestCriteria> {
        return value ? cloneDeep(value) : {};
    }
}

export class CriteriaHistoryEntry {
    timestamp: Date;
    slot: Record<string, RequestCriteria>;
}
